import { useMemo, useCallback } from "react";
import { useTheme } from "@mui/material";
import { Bar } from "@visx/shape";
import { scaleBand, scaleLinear, scaleOrdinal } from "@visx/scale";
import { useSpring, animated } from "react-spring";
import { AxisBottom, AxisLeft } from "@visx/axis";
import { defaultPieChartColorRange } from "../../pie-chart/utils";
import { useTooltip, TooltipWithBounds } from '@visx/tooltip';
import { localPoint } from '@visx/event';
import { getChartTooltipStyles, getChartTooltipSwatchStyle } from "../../../utils/chart-styles";

const DEFAULT_TEXT_COLOR = "#000";

const HORIZONTAL_MARGIN = 50;
const LINE_HEIGHT = 17;
const BASE_BOTTOM_MARGIN = 45; // Base margin for axis line, tick marks, and single-line label

// Calculate dynamic bottom margin based on max words in labels
const calculateBottomMargin = (labels: string[]) => {
  const maxWords = Math.max(...labels.map(label => String(label).split(" ").length), 1);
  return BASE_BOTTOM_MARGIN + (maxWords - 1) * LINE_HEIGHT;
};

// Custom tick component for wrapping multi-word labels
const CustomTickLabel = ({ 
  x = 0, 
  y = 0, 
  text, 
  fill, 
  fontSize,
  textAnchor 
}: any) => {
  const words = String(text).split(" ");
  const startY = y + (words.length - 1) * (LINE_HEIGHT / 2);

  return (
    <text x={x} y={startY} fill={fill} fontSize={fontSize} textAnchor={textAnchor}>
      {words.map((word: string, idx: number) => (
        <tspan key={`word-${idx}`} x={x} dy={idx === 0 ? 0 : LINE_HEIGHT}>
          {word}
        </tspan>
      ))}
    </text>
  );
};

// Vertical tick label component for narrow widths with many labels
const VerticalTickLabel = ({ 
  x = 0, 
  y = 0, 
  text, 
  fill, 
  fontSize
}: any) => {
  const words = String(text).split(" ");
  const verticalLineHeight = 14;
  
  return (
    <g transform={`rotate(-90, ${x}, ${y})`}>
      <text 
        x={x} 
        y={y} 
        fill={fill} 
        fontSize={fontSize} 
        textAnchor="end"
        dy="0.35em"
        dx="-0.5em"
      >
        {words.map((word: string, idx: number) => (
          <tspan key={`word-${idx}`} x={x} dx="-0.5em" dy={idx === 0 ? 0 : verticalLineHeight}>
            {word}
          </tspan>
        ))}
      </text>
    </g>
  );
};

export type BarChartProps<DataType> = {
  getLabel: (d: DataType) => string;
  getValue: (d: DataType) => number;
  getId: (d: DataType) => string;
  data: DataType[];
  events?: boolean;
  textColor?: string;
  lineColor?: string;
};

interface BarChartComponentProps<DataType> extends BarChartProps<DataType> {
  width: number;
  height: number;
}

interface TooltipData {
  label: string;
  value: number;
  color: string;
}

export const BarChartComponent = <DataType,>({
  width,
  height,
  events = false,
  data,
  getLabel,
  getId,
  getValue,
  lineColor,
  textColor = DEFAULT_TEXT_COLOR,
}: BarChartComponentProps<DataType>) => {
  const theme = useTheme();
  const tooltipStyles = getChartTooltipStyles(theme);
  const {
    showTooltip,
    hideTooltip,
    tooltipData,
    tooltipLeft,
    tooltipTop,
    tooltipOpen,
  } = useTooltip<TooltipData>();
  // Calculate dynamic bottom margin based on label word count
  const labels = data.map(getLabel);
  
  // Determine if we should use vertical labels
  const shouldUseVerticalLabels = labels.length > 6 && width < 900;
  
  const bottomMargin = useMemo(() => 
    shouldUseVerticalLabels ? 120 : calculateBottomMargin(labels), 
    [labels, shouldUseVerticalLabels]
  );
  const verticalMargin = bottomMargin + 20; // top margin + dynamic bottom
  
  const xMax = width - HORIZONTAL_MARGIN;
  const yMax = height - verticalMargin;

  // scales, memoize for performance
  const xScale = useMemo(
    () =>
      scaleBand<string>({
        range: [0, xMax],
        round: true,
        domain: data.map(getLabel),
        padding: 0.4,
      }),
    [xMax, getLabel, data]
  );
  const yScale = useMemo(
    () =>
      scaleLinear<number>({
        range: [yMax, 0],
        round: true,
        domain: [0, Math.max(...data.map(getValue))],
      }),
    [yMax, getValue, data]
  );

  const { scale } = useSpring({
    from: { scale: 0 },
    to: { scale: 1 },
  });
  const AnimatedBar = animated(Bar) as any;
  const getDataColor = scaleOrdinal({
    domain: data.map((sliceData) => getId(sliceData)),
    range: defaultPieChartColorRange,
  });

  // Handle bar hover
  const handleBarHover = useCallback(
    (event: React.MouseEvent<SVGRectElement>, d: DataType) => {
      const point = localPoint(event);
      if (!point) return;
      
      showTooltip({
        tooltipData: {
          label: getLabel(d),
          value: getValue(d),
          color: getDataColor(getId(d)),
        },
        tooltipLeft: point.x,
        tooltipTop: point.y,
      });
    },
    [getLabel, getValue, getId, getDataColor, showTooltip]
  );

  return width < 10 ? null : (
    <div style={{ position: 'relative' }}>
      <svg
        width={width}
        height={height}
        className="bar-chart"
        style={{
          overflow: "visible",
          display: "block",
        }}
      >
        <defs>
          <filter id="bar-chart-shadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="2" stdDeviation="3" floodOpacity="0.3" />
          </filter>
        </defs>
        <rect width={width} height={height} fill="url(#teal)" rx={14} />
          <g
            className="bars"
            dx={HORIZONTAL_MARGIN}
            style={{ transformOrigin: "bottom center" }}
          >
            {data.map((d) => {
              const letter = getLabel(d);
              const barWidth = xScale.bandwidth();
              const barHeight = yMax - (yScale(getValue(d)) ?? 0);
              const barX = xScale(letter);
              const isHovered = tooltipOpen && tooltipData?.label === letter;
              return (
                <AnimatedBar
                  style={{
                    transformOrigin: "bottom",
                    cursor: 'pointer',
                    filter: isHovered ? 'url(#bar-chart-shadow)' : 'none',
                  }}
                  key={`bar-${letter}`}
                  x={(barX ?? 0) + HORIZONTAL_MARGIN}
                  y={scale.interpolate((s) => yMax - s * barHeight)}
                  width={barWidth}
                  height={scale.interpolate((s) => s * barHeight)}
                  fill={getDataColor(getId(d))}
                  onClick={() => {
                    if (events) alert(`clicked: ${JSON.stringify(d)}`);
                  }}
                  onMouseMove={(e: React.MouseEvent<SVGRectElement>) => handleBarHover(e, d)}
                  onMouseLeave={hideTooltip}
                />
              );
            })}

          <AxisLeft
            axisClassName="yaxis"
            scale={yScale}
            stroke={lineColor ?? textColor}
            tickStroke={lineColor ?? textColor}
            left={HORIZONTAL_MARGIN + 10}
            tickLabelProps={{
              fill: textColor,
              fontSize: 11,
              textAnchor: "end",
              dx: "-0.33em",
            }}
            // hideAxisLine
          />

          <AxisBottom
            axisClassName="xaxis"
            scale={xScale}
            stroke={lineColor ?? textColor}
            tickStroke={lineColor ?? textColor}
            top={yMax}
            left={HORIZONTAL_MARGIN}
            // hideAxisLine
            tickComponent={({ formattedValue, ...tickProps }) => {
              return shouldUseVerticalLabels ? (
                <VerticalTickLabel
                  {...tickProps}
                  text={String(formattedValue)}
                  fill={textColor}
                  fontSize={11}
                />
              ) : (
                <CustomTickLabel
                  {...tickProps}
                  text={String(formattedValue)}
                  fill={textColor}
                  fontSize={11}
                  textAnchor="middle"
                />
              );
            }}
          />
        </g>
      </svg>
      {/* Tooltip */}
      {tooltipOpen && tooltipData && (
        <TooltipWithBounds
          left={tooltipLeft}
          top={tooltipTop}
          style={tooltipStyles}
        >
          <div style={{ fontWeight: 600, marginBottom: '4px' }}>
            {tooltipData.label}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={getChartTooltipSwatchStyle(tooltipData.color)} />
            <span style={{ fontSize: '12px', fontWeight: 500 }}>{tooltipData.value.toLocaleString()}</span>
          </div>
        </TooltipWithBounds>
      )}
    </div>
  );
};
